import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import InstallAppModal from './InstallAppModal';
import useInstallPrompt from '@/lib/useInstallPrompt';

/**
 * PWAInstallPrompt — small unobtrusive "Install App" bottom sheet.
 * Instead of trying to fire the native install itself (which only works
 * on Chrome/Edge, not iOS Safari), this prompt ALWAYS opens the rich
 * InstallAppModal which shows platform-correct instructions.
 *
 * Respects user dismissal via localStorage (14-day cooldown) and
 * disappears automatically when the app is already installed.
 */
const STORAGE_KEY = 'plexura_pwa_dismissed_at';
const REPROMPT_MS = 14 * 24 * 60 * 60 * 1000;

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
}

export default function PWAInstallPrompt() {
  const { deferredPrompt, installed } = useInstallPrompt();
  const [bannerVisible, setBannerVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (isStandalone() || installed) return;
    const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < REPROMPT_MS) return;
    const t = setTimeout(() => setBannerVisible(true), 2000);
    return () => clearTimeout(t);
  }, [installed]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setBannerVisible(false);
  };

  const openModal = () => {
    setModalOpen(true);
    setBannerVisible(false); // hide banner once they engaged
  };

  return (
    <>
      {bannerVisible && !installed && (
        <div
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[60] animate-[slideUp_300ms_ease-out]"
          data-testid="pwa-install-prompt"
        >
          <div
            className="rounded-2xl shadow-2xl border overflow-hidden"
            style={{ background: '#FDFBF7', borderColor: '#E6E2D8' }}
          >
            <div className="p-4 sm:p-5 flex items-start gap-3">
              <img
                src="/plexura-logo.png"
                alt="Plexura AI Scribe"
                className="shrink-0 w-11 h-11 rounded-xl object-contain p-1"
                style={{ background: '#FFFFFF', border: '1px solid #E6E2D8' }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#1C1C19]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Install Plexura AI Scribe
                </p>
                <p className="text-xs text-[#5C5C54] mt-0.5 leading-relaxed">
                  Add it to your home screen — launches full-screen like a native app.
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={openModal}
                    data-testid="pwa-install-button"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
                    style={{ background: '#2D3A33', color: '#FDFBF7' }}
                  >
                    <Download className="w-3.5 h-3.5" />
                    How to Install
                  </button>
                  <button
                    onClick={dismiss}
                    data-testid="pwa-dismiss-button"
                    className="text-xs text-[#8A8A80] hover:text-[#1C1C19] transition-colors px-2 py-2"
                  >
                    Not now
                  </button>
                </div>
              </div>
              <button
                onClick={dismiss}
                data-testid="pwa-close-button"
                aria-label="Dismiss install prompt"
                className="shrink-0 p-1 rounded-md text-[#8A8A80] hover:bg-[#F3F0E6] hover:text-[#1C1C19] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <style>{`
            @keyframes slideUp {
              from { transform: translateY(20px); opacity: 0; }
              to   { transform: translateY(0);    opacity: 1; }
            }
          `}</style>
        </div>
      )}

      <InstallAppModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        deferredPrompt={deferredPrompt}
      />
    </>
  );
}
